import React, { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'pannellum';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'pannellum/build/pannellum.css';
import styled, { createGlobalStyle } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  PannellumCubeMapConfig,
  PannellumEquirectangularConfig,
  PannellumMultiresConfig,
  PannellumTour,
  PannellumViewer,
  // eslint-disable-next-line import/no-unresolved
} from './pannellum-types';
import { Spinner } from '../spinner/Spinner';

declare const pannellum: {
  viewer(
    container: string | HTMLElement,
    config:
      | PannellumEquirectangularConfig
      | PannellumCubeMapConfig
      | PannellumMultiresConfig
      | PannellumTour,
  ): PannellumViewer;
};

const PannellumStyles = createGlobalStyle`
    .pnlm-load-box {
        display: none !important;
    }

    .pnlm-container {
        background: #fff !important;
    }

    .pnlm-lmsg {
        display: none !important;
    }

    .pnlm-grab {
        cursor: grab !important;
    }

    .pnlm-grabbing {
        cursor: grabbing !important;
    }

    .pnlm-about-msg {
        display: none !important;
    }

    .pnlm-error-msg {
        display: none !important;
    }
`;

const StyledDiv = styled.div<{ relative?: boolean }>`
  ${({ relative }) => relative && 'position: relative;'}
  width: 100%;
  height: 100%;
`;

const LoaderWrapper = styled.div`
  position: absolute;
  z-index: 999;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const PanoramaViewer = ({
  image,
  className,
}: {
  image: string;
  className?: string;
}): JSX.Element => {
  const panoramaRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (panoramaRef.current && pannellum) {
      setLoading(true);

      const viewer = pannellum.viewer(panoramaRef.current, {
        type: 'equirectangular',
        panorama: image,
        autoLoad: true,
        showControls: false,
        doubleClickZoom: false,
        keyboardZoom: false,
        mouseZoom: false,
        autoRotate: -2,
        hfov: 95,
        showZoomCtrl: false,
      });

      viewer.on('load', () => {
        if (isMounted) {
          setLoading(false);
        }
      });

      viewer.on('error', (e) => {
        if (isMounted) {
          console.error('Error loading panorama:', e);
        }
      });

      return () => {
        isMounted = false;
        viewer.destroy();
      };
    }

    return undefined;
  }, [image]);

  return (
    <StyledDiv relative className={className}>
      <PannellumStyles />
      <StyledDiv>
        {loading && (
          <LoaderWrapper data-testid="panorama-viewer-loading">
            <Spinner aria-hidden="true" />
          </LoaderWrapper>
        )}
        <div
          data-testid="panorama-viewer-container"
          aria-label={t('panoramaViewer.ariaLabel')}
          role="img"
          ref={panoramaRef}
        />
      </StyledDiv>
    </StyledDiv>
  );
};

export default PanoramaViewer;
